import Image from 'next/image';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { useTenantContext } from '../../context/TenantContext';
import { setSearchBoxValuesToStorage, useSearchBoxValuesFromStorage } from '../../utils/searchBoxValuesFromStorage';
import { withDefaultBookingValues } from '../../utils/trip';
import Formed from '../Formed';
import Page from '../Page';
import SearchBox, { getInitialSearchBoxValues, getSearchValidationSchema, SearchBarValues } from '../SearchBox';
import { SearchVariant } from '../SearchForm';
import ContentWrapper from '../ContentWrapper';
import { Flex } from '@rebass/grid';

const Index = () => {
    const { brandConfig } = useTenantContext();
    return (
        <StyledPage>
            <ContentWrapper>
                {brandConfig.indexImage && (
                    <ImageContainer>
                        <Image
                            alt={brandConfig.seo.siteName}
                            src={brandConfig.indexImage}
                            layout="fill"
                            objectFit="cover"
                            quality={100}
                            loading="lazy"
                            placeholder="blur"
                        />
                    </ImageContainer>
                )}
                <Wrapper>
                    <IndexSearchBox variant={brandConfig.name === 'Qualitylodgings' ? 'homeBarXl' : 'homeBar'} />
                </Wrapper>
            </ContentWrapper>
        </StyledPage>
    );
};

export default Index;

export const IndexSearchBox = ({ variant = 'homeBar' }: { variant?: SearchVariant }) => {
    const router = useRouter();
    const validationSchema = getSearchValidationSchema();
    const { brandConfig } = useTenantContext();
    const storedSearch = useSearchBoxValuesFromStorage();

    return (
        <Formed<SearchBarValues>
            handleSubmit={async values => {
                const { accommodationType, booking, brand, accommodationId } = values;
                setSearchBoxValuesToStorage(values);
                const orFilters = brand !== 'all' && !!brand && `_brand.${brand}`;
                await router.push({
                    pathname: `/search`,
                    query: {
                        ...withDefaultBookingValues(booking),
                        ...(accommodationType && { accommodationType }),
                        ...(accommodationId && { accommodationId }),
                        ...(brand && !!orFilters && { orFilters }),
                        trip: undefined,
                    },
                });
            }}
            initialValues={getInitialSearchBoxValues(brandConfig, storedSearch)}
            enableReinitialize
            validationSchema={validationSchema}
            skipPrompt
        >
            {({ isSubmitting }) => (
                <Flex justifyContent={variant === 'homeBarXl' ? 'center' : undefined}>
                    <SearchBox
                        variant={variant}
                        isSubmitting={isSubmitting}
                        active={brandConfig.searchBar?.active}
                        hideHelper
                        hideCalendarIcon
                    />
                </Flex>
            )}
        </Formed>
    );
};

const Wrapper = styled.div`
    max-width: 100%;
    margin: 0 auto;
    z-index: ${({ theme }) => theme.zIndices.docked};
    position: relative;
`;

const StyledPage = styled(Page)`
    min-height: 100vh;
    display: flex;
    align-items: center;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        min-height: 70vh;
    }
`;

const ImageContainer = styled.div`
    display: inline-block;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    height: unset;
    position: absolute;

    ::after {
        padding-top: calc(9 / 16 * 100%);
        content: '';
        display: block;
    }
`;
